import { SchedulerEventComponent } from '../../schedule/event/schedulerEvent.component';
import { GiveFeedbackComponent } from '../give-feedback/give-feedback.component';
import { DialogService } from 'primeng/dynamicdialog';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';
import { Observable, Subscription, map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Customer } from '../../../models/Customer';
import { CustomerService } from '../../../services/customer.service';
import { UserType } from '../../../models/User';
import { RouterLink } from '@angular/router';
import { HeaderCustomerSelectorComponent } from '../../shared/header-customer-selector/header-customer-selector.component';
import { HeaderItemComponent } from './header-item/header-item.component';
import { ManualButtonComponent } from '../manual-button/manual-button.component';
import { Menu } from 'primeng/menu';
import { LayoutService } from '../../../layout/service/layout.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],

  imports: [
    RouterLink,
    TranslatePipe,
    HeaderCustomerSelectorComponent,
    HeaderItemComponent,
    ManualButtonComponent,
    Menu
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  private userProfileSubscription$: Subscription | null;
  private selectedCustomer$: Subscription | null = null;
  private langChange$: Subscription | null = null;

  public profileLabel = '';
  public passwordLabel = '';
  public logoutLabel = '';
  public items: Array<MenuItem>;
  public organization = '';
  public customerUrl = 'customer/basic';
  public donorUrl = 'donor/basic';
  public externalDonorUrl = 'donor/external';
  public showEndMenu = false;
  public customer: Customer | undefined | null = undefined;
  public profileMenu: Array<MenuItem>;
  public userType = UserType.customer;
  public customerType!: string;
  public userTypeCustomerDonor = UserType.customerDonor;

  constructor(
    private authService: AuthService,
    private customerService: CustomerService,
    private translateService: TranslateService,
    private dialogService: DialogService,
    private layoutService: LayoutService
  ) {
    this.items = new Array<MenuItem>();
    this.profileMenu = new Array<MenuItem>();
    this.userProfileSubscription$ = null;
  }

  async ngOnInit(): Promise<void> {
    // Translate menu items and build menu
    this.translateProfileMenu().subscribe(() => this.buildMenu());
    // The menus translate their labels once: build them again in a new
    // language (also the user's own one, if it loads after the header).
    this.langChange$ = this.translateService.onLangChange.subscribe(() => {
      this.translateProfileMenu().subscribe();
      this.buildMenu();
    });

    this.customerType = 'c';
    this.selectedCustomer$ =
      this.customerService.selectedCustomerObservable$.subscribe((result) => {
        this.customer = result;
      });
  }

  /**
   * Yksityisen terveydenhuollon asiakkaan osoiterivi yläpalkkiin:
   * "Katuosoite, Postinumero Kaupunki". Tyhjät kentät jätetään pois,
   * eikä ylimääräistä pilkkua/väliä jää näkyviin.
   */
  get contactAddressLine(): string {
    const ci = this.customer?.contactInfo;
    if (!ci) return '';
    const cityPart = [ci.postCode, ci.city]
      .filter((part) => part && part.trim())
      .join(' ');
    return [ci.address, cityPart]
      .filter((part) => part && part.trim())
      .join(', ');
  }

  /** Asiakkaan puhelinnumero yläpalkin toiselle riville. */
  get contactPhone(): string {
    return this.customer?.contactInfo?.phone?.trim() ?? '';
  }

  /**
   * Tarkista onko asiakas IVF-laboratorio käyttäjä.
   * Käyttää laboratoryModule arvoa tai fallback genderiin vanhoille asiakkaille.
   */
  isIvfLaboratory(): boolean {
    if (!this.customer) return false;
    return (
      this.customer.laboratoryModule === 'ivf-laboratory' ||
      (!this.customer.laboratoryModule && this.customer.gender === 'female')
    );
  }

  // async changeCustomer(value: any): Promise<void> {
  //   // set selected customer
  //   await this.customerService.setSelectedCustomer(value.value);
  // }

  ngOnDestroy(): void {
    if (this.userProfileSubscription$) {
      this.userProfileSubscription$.unsubscribe();
    }
    if (this.selectedCustomer$) {
      this.selectedCustomer$.unsubscribe();
    }
    this.langChange$?.unsubscribe();
  }

  /** Builds the profile menu in the current language. */
  private translateProfileMenu(): Observable<void> {
    return this.translateService
      .get([
        'common.profile',
        'common.password.change-password',
        'common.menu-settings',
        'common.button.logout'
      ])
      .pipe(
        map((result) => {
          this.profileLabel = result['common.profile'];
          this.passwordLabel = result['common.password.change-password'];
          this.logoutLabel = result['common.button.logout'];

          this.profileMenu = [
            {
              label: this.profileLabel,
              icon: 'pi pi-user',
              routerLink: 'profile'
            },
            { separator: true },
            {
              label: this.passwordLabel,
              icon: 'pi pi-key',
              routerLink: 'change/password'
            },
            { separator: true },
            {
              label: result['common.menu-settings'],
              icon: 'pi pi-cog',
              command: () => this.layoutService.toggleConfigSidebar()
            },
            { separator: true },
            {
              label: this.logoutLabel,
              icon: 'pi pi-sign-out',
              routerLink: 'logout'
            }
          ];
        })
      );
  }

  giveFeedback(): void {
    const ref = this.dialogService.open(GiveFeedbackComponent, {
      header: 'Feedback',
      width: '75%',
      height: '75%',
      closable: true,
      draggable: true,
      resizable: true,
      baseZIndex: 10000
    });

    ref!.onClose.subscribe((retValue: string) => {
      if (retValue === 'success') {
      }
    });
  }

  addTodo(): void {
    const eventData = {
      eventType: 'todo',
      eventDate: new Date()
    };

    const eventInfo = null;

    const ref = this.dialogService.open(SchedulerEventComponent, {
      header: 'Todo',
      data: {
        eventData,
        eventInfo
      },
      width: '75%',
      height: '75%',
      closable: true,
      draggable: true,
      resizable: true,
      baseZIndex: 10000
    });

    ref!.onClose.subscribe((retValue: string) => {
      if (retValue === 'success') {
      }
    });
  }

  buildMenu(): void {
    const menuModules = this.authService.getAppModules('header');

    // Fetch user profile and check active menu items.
    this.items = [];
    this.userProfileSubscription$ =
      this.authService.userProfileObservable$.subscribe((profile) => {
        this.organization = profile?.activeOrganizationUnit?.name || '';
        this.showEndMenu = false;

        if (profile) {
          this.showEndMenu = true;
          if (profile!.groups) {
            this.items = this.authService.getUserActiveAppModules(
              null,
              menuModules,
              profile,
              false,
              'header'
            );
          }
        }
      });
  }

  onItemSelected(menuItem: MenuItem): void {
    // TODO: kuinka saisi kutsuttua JSON:sta tulevia metodeja suoraan?
    switch (menuItem.id) {
      case 'todo':
        this.addTodo();
        break;
      case 'feedback':
        this.giveFeedback();
    }
  }

  async copyPersonalId(value?: string, event?: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!value) {
      return;
    }
    try {
      if (navigator && 'clipboard' in navigator) {
        await navigator.clipboard.writeText(value);
        return;
      }
    } catch (_) {
      // fall through to legacy method
    }

    // Legacy fallback
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
